import { deriveField } from "../../utils/fieldGeometry";
import { orientationsFromConfig, systemDesignForOrientation } from "../config/orientations";

const asNumber = (value) => {
  if (value == null || value === "") return null;
  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizedTopology = (value) =>
  String(value || "").trim().toLowerCase().replaceAll("_", "-").replaceAll(" ", "-");

export const DEFAULT_SLD_OPTIONS = Object.freeze({
  use_generation_meter: true,
  use_spd: true,
  use_nvr: true,
  include_bess: false,
  main_breaker_mode: "auto",
  main_breaker_rating: "",
  main_breaker_voltage: 400,
  main_breaker_type: "MCCB",
  inverter_breaker_mode: "auto",
  inverter_breaker_rating: "",
  inverter_breaker_voltage: 400,
  inverter_breaker_type: "MCCB",
  feeder_cable_label: "TBD",
  client_panel_cable_label: "TBD",
  dc_cable_label: "TBD",
  meter_accuracy_class: 0.5,
  meter_primary_current: "",
  meter_secondary_current: 5,
  meter_burden_rating: 15,
  contactor_label: "CONTACTOR",
  contactor_rating: "",
  spd_voltage: 400,
  spd_current_capacity: 40,
  spd_type: 2,
  released_for: "PRELIMINARY",
  designed_by: "METEN",
  drawn_by: "METEN",
  checked_by: "TBD",
  approved_by: "TBD",
  drawing_name: "PV SINGLE-LINE DIAGRAM",
  project_title: "",
});

export function hasConfiguredBess(config) {
  return Array.isArray(config?.bess?.selected) && config.bess.selected.length > 0;
}

export function supportedBessTopology(config) {
  const topology = normalizedTopology(config?.bess?.topology || config?.bess?.sldTopology);
  return topology === "ac-coupled" ||
    (topology === "dc-coupled" && Object.keys(config?.systemDesign || {}).length === 1);
}

function activeBattery(config) {
  const selected = Array.isArray(config?.bess?.selected) ? config.bess.selected : [];
  const activeName = config?.bess?.activeName;
  return selected.find((battery) => battery?.name === activeName) || selected[0] || null;
}

function firstPowerFactor(config) {
  const dynamic = config?.pvLosses?.ac?.wiringDynamic || {};
  const main = asNumber(dynamic?.main?.powerFactor);
  if (main != null && main > 0 && main <= 1) return main;
  for (const [key, value] of Object.entries(dynamic)) {
    if (key === "main") continue;
    const candidate = asNumber(value?.powerFactor);
    if (candidate != null && candidate > 0 && candidate <= 1) return candidate;
  }
  return 1.0;
}

function projectMetadata(config, currentProject, options) {
  const client = config?.clientInfo || {};
  const meta = config?.metaInfo || {};
  return {
    id: currentProject?.id || null,
    name:
      options?.project_title ||
      client?.project_title ||
      currentProject?.name ||
      config?.projectName ||
      "Solar Power Plant",
    client: client?.clName || "TBD",
    site: client?.client_address || config?.address || "TBD",
    mount_type: config?.mountType || "TBD",
    tilt_deg: asNumber(config?.panelOrientation?.tilt),
    azimuth_deg: asNumber(config?.panelOrientation?.azim),
    drawing_number: meta?.document_id || "TBD",
    revision: meta?.study_version || "Rev 0",
    issue_date: meta?.prep_date || new Date().toISOString().slice(0, 10),
    status: options?.released_for || "PRELIMINARY",
    prepared_by: options?.designed_by || config?.engInfo?.eng_name || "METEN",
    checked_by: options?.checked_by || "TBD",
    approved_by: options?.approved_by || meta?.study_approvedBy || "TBD",
  };
}

function bessPayload(config, options, batteryDetails) {
  const battery = activeBattery(config);
  const chars = batteryDetails?.characteristics || battery?.characteristics || {};
  const count = Math.max(1, asNumber(battery?.count) || 1);
  const energyPerUnit = asNumber(
    batteryDetails?.energy ?? battery?.energy ?? chars?.Energy ?? chars?.energy
  );
  const powerPerUnit = asNumber(
    batteryDetails?.p_max_d ?? battery?.p_max_d ?? chars?.P_max_d ?? chars?.p_max_d
  );
  const topology = normalizedTopology(config?.bess?.topology || config?.bess?.sldTopology);
  return {
    configured: Boolean(battery),
    include: Boolean(options?.include_bess && battery && supportedBessTopology(config)),
    topology,
    name: battery?.name || batteryDetails?.name || "TBD",
    energy_kwh: energyPerUnit == null ? null : energyPerUnit * count,
    power_kw: powerPerUnit == null ? null : powerPerUnit * count,
    voltage_v: asNumber(chars?.Vnom ?? chars?.voltage_nom),
    dc_dc_efficiency_pct: asNumber(config?.bess?.dcDcEfficiencyPct),
    dc_port_charge_kw: asNumber(config?.bess?.dcPortChargeKw),
    dc_port_discharge_kw: asNumber(config?.bess?.dcPortDischargeKw),
    pcs_model: config?.bess?.pcs?.model || "TBD",
    pcs_power_kw: asNumber(config?.bess?.pcs?.powerKw),
    breaker_label: config?.bess?.sldBreakerLabel || "TBD",
    cable_label: config?.bess?.sldCableLabel || "TBD",
  };
}


/**
 * The rows the strings will be placed on, for the drawing.
 *
 * The SLD's string table now carries a row and column range per string, so
 * the drawing and the layout tab name the same positions. The rows come from
 * the saved physical layout: the parcel's tables when the boundary was fitted,
 * otherwise the typed row count shared out exactly as the layout tab does it
 * (same function, so the same split). Hand placements travel with them.
 * Nothing here decides where a string goes -- the server does that once.
 */
export function fieldLayoutForSld(config, moduleDetails = null) {
  const spacing = config?.pv_design?.spacingInfo || config?.pv_design?.spacing || null;
  // Several planes: the saved layout is the first plane's, so it holds only
  // the strings attached to that plane.
  const planes = orientationsFromConfig(config);
  const systemDesign = planes.length > 1
    ? systemDesignForOrientation(config?.systemDesign, planes, planes[0].id)
    : config?.systemDesign;
  return fieldLayoutFromSpacing(spacing, systemDesign, moduleDetails);
}

/**
 * The plant's planes, for a plant with more than one (null otherwise, so a
 * single-plane payload is unchanged). Each further plane carries its own rows
 * from its own saved layout; the first plane's rows stay in `fieldLayout`.
 */
export function orientationsForSld(config, moduleDetails = null) {
  const planes = orientationsFromConfig(config);
  if (planes.length < 2) return null;
  return planes.map((plane) => ({
    id: plane.id,
    name: !plane.autoName && plane.name ? plane.name : "",
    tilt: asNumber(plane.tilt),
    azim: asNumber(plane.azim),
    fieldLayout: plane.primary
      ? null
      : fieldLayoutFromSpacing(
        plane.spacingInfo,
        systemDesignForOrientation(config?.systemDesign, planes, plane.id),
        moduleDetails,
      ),
  }));
}

function fieldLayoutFromSpacing(spacing, systemDesign, moduleDetails) {
  if (!spacing) return null;
  const tiers = asNumber(spacing.row_vertical_modules) || 1;
  let rowModules = Array.isArray(spacing.row_modules) && spacing.row_modules.length
    ? spacing.row_modules
    : null;
  if (!rowModules) {
    const total = Object.values(systemDesign || {}).flat().reduce((sum, mppt) =>
      sum + (mppt?.inputs || []).reduce((s, i) => s + (asNumber(i?.modules_per_string) || 0), 0), 0);
    const field = deriveField({
      totalModules: total, pitch: 1, moduleWidth: 1, verticalModules: tiers,
      fieldRows: asNumber(spacing.field_rows),
    });
    rowModules = field ? field.rowModules : null;
  }
  if (!rowModules || !rowModules.length) return null;
  const chars = moduleDetails?.characteristics || {};
  return {
    rowModules,
    rowLabels: Array.isArray(spacing.row_labels) ? spacing.row_labels : null,
    tiers,
    overrides: spacing.string_overrides || null,
    ...(spacing.string_routing ? { routing: spacing.string_routing } : {}),
    // For the plan sheet: the tables where they stand and the boundary when the
    // layout was fitted to a parcel; the pitch and module width for equal bars
    // when it was not.
    parcelTables: Array.isArray(spacing.parcel_tables) ? spacing.parcel_tables : null,
    parcelOutline: spacing.parcel_outline || null,
    pitchM: asNumber(spacing.minimum_distance_m ?? spacing.pitch_m ?? spacing.pitch),
    moduleWidthM: asNumber(chars.Width ?? chars.width) || null,
  };
}

export function buildSldPayload({
  config,
  currentProject,
  moduleDetails,
  inverterDetails,
  batteryDetails = null,
  options,
}) {
  if (!config?.systemDesign || !Object.keys(config.systemDesign).length) {
    throw new Error("No saved PV electrical design is available.");
  }
  if (!config?.read_module || !config?.read_inverter) {
    throw new Error("Select a PV module and inverter before generating the SLD.");
  }
  const inverterChars = inverterDetails?.characteristics || {};
  const catalogVoltage = asNumber(
    inverterChars?.Vac ?? inverterChars?.Vac_nom ?? inverterChars?.["Nominal AC Voltage"]
  );
  const systemVoltage =
    asNumber(options?.main_breaker_voltage) ||
    asNumber(options?.inverter_breaker_voltage) ||
    catalogVoltage ||
    400;

  const orientations = orientationsForSld(config, moduleDetails);
  return {
    schemaVersion: 2,
    project: projectMetadata(config, currentProject, options),
    equipment: {
      module: {
        name: config.read_module,
        characteristics: moduleDetails?.characteristics || {},
      },
      inverter: {
        name: config.read_inverter,
        characteristics: inverterDetails?.characteristics || {},
      },
    },
    inverterName: config.read_inverter,
    moduleName: config.read_module,
    systemDesign: config.systemDesign,
    fieldLayout: fieldLayoutForSld(config, moduleDetails),
    ...(orientations ? { orientations } : {}),
    electrical: {
      system_voltage_v: systemVoltage,
      power_factor: firstPowerFactor(config),
      destination_panel: "ACDB / AC collection bus",
      grid_connection_level: config?.grid?.connectionLevel || "TBD",
    },
    options: {
      ...options,
      main_breaker: {
        mode: options?.main_breaker_mode || "auto",
        rating_a: asNumber(options?.main_breaker_rating),
        voltage_v: asNumber(options?.main_breaker_voltage),
        type: options?.main_breaker_type || "MCCB",
      },
      inverter_breaker: {
        mode: options?.inverter_breaker_mode || "auto",
        rating_a: asNumber(options?.inverter_breaker_rating),
        voltage_v: asNumber(options?.inverter_breaker_voltage),
        type: options?.inverter_breaker_type || "MCCB",
      },
    },
    bess: bessPayload(config, options, batteryDetails),
  };
}

export function parseSldResponseMetadata(headers) {
  let warnings = [];
  const encoded = headers?.get?.("X-SLD-Warnings");
  if (encoded) {
    try {
      warnings = JSON.parse(decodeURIComponent(encoded));
    } catch {
      warnings = [
        {
          code: "WARNING_HEADER_INVALID",
          severity: "warning",
          message: "The PDF was generated, but its validation metadata could not be read.",
        },
      ];
    }
  }
  return {
    warnings,
    pageCount: asNumber(headers?.get?.("X-SLD-Pages")),
    fingerprint: headers?.get?.("X-SLD-Fingerprint") || "",
  };
}
